import type { Comp, GameObj, ScaleComp } from "kaplay";
import { config } from "../config";
import { emit } from "../core/events";
import { k } from "../core/kaplay";
import { hash, mulberry32 } from "../core/rng";
import type { RunState } from "../core/runState";
import type { AirState } from "../systems/air";
import {
    NO_TRICKS, classifyLanding, pressTrick, stepTricks, trickProgress, trickValue, type AirTricks,
} from "../systems/tricks";
import type { Rider } from "./rider";

export interface AirTricksComp extends Comp {
    tricks: AirTricks;
}

type TrickRider = Rider & GameObj<ScaleComp> & AirTricksComp;

const cfg = config.tricks;
const player = config.player;
const POSES: Record<string, string> = { superman: "superman", noHander: "no_hander" };

function resetVisuals(r: TrickRider) {
    r.angle = 0;
    r.scale = k.vec2(1, 1);
    r.spriteScale = k.vec2(1, 1);
}

function showTricks(r: TrickRider) {
    resetVisuals(r);
    r.pose = "air";
    r.angle = r.tricks.spinDeg;
    const { active } = r.tricks;
    if (!active) return;

    const t = trickProgress(active, cfg);
    if (active.name === "spin360") r.angle += 360 * t;
    if (active.name === "backflip") r.spriteScale = k.vec2(1, Math.cos(t * Math.PI * 2));
    if (POSES[active.name]) {
        r.pose = POSES[active.name];
        r.angle += Math.sin(k.time() * 25) * 6;
    }
}

function fly(r: TrickRider, roll: () => number) {
    if (r.intent.jump) {
        const { state, started } = pressTrick(r.tricks, roll(), cfg);
        r.tricks = state;
        if (started) emit("trickStart", { trick: started });
    }
    const { state, completed } = stepTricks(r.tricks, k.dt(), r.intent.spin, cfg);
    r.tricks = state;
    completed.forEach((c) => emit("trickComplete", c));
    showTricks(r);
}

function land(r: TrickRider, run: RunState, air: AirState) {
    resetVisuals(r);
    const bigAir = air.time >= cfg.air.bigAirS;
    const landing = classifyLanding(r.tricks, r.motion.heading, bigAir, cfg);
    emit("land", { clean: landing.clean, airtime: air.time });

    if (!landing.clean) {
        emit("trickFail", { trick: landing.reason });
        r.wipeout("medium");
        return;
    }

    const value = trickValue(r.tricks, cfg);
    if (!bigAir && value === 0) return;

    const muddy = bigAir && run.surface === "mud" ? cfg.air.mudLandLoss : 0;
    const speed = (r.motion.speed + cfg.air.cleanLandSpeedBonus) * (1 - muddy);
    r.motion = { ...r.motion, speed };
    r.refillBoost(player.boostRefillCleanLanding + value * player.boostRefillPerTrickPoint);
}

export function airTricks(run: RunState): AirTricksComp {
    const roll = mulberry32(hash(run.seed, 0x747269, 0));

    return {
        id: "airTricks",
        require: ["rider", "scale"],
        tricks: NO_TRICKS,

        add(this: TrickRider) {
            this.onStateEnter("air", () => (this.tricks = NO_TRICKS));
            this.onStateEnter("crashed", () => {
                this.scale = k.vec2(1, 1);
                this.spriteScale = k.vec2(1, 1);
            });
            this.onStateUpdate("air", () => fly(this, roll));
            this.onStateEnter("riding", (landed?: AirState) => {
                if (landed) land(this, run, landed);
                this.tricks = NO_TRICKS;
            });
        },
    };
}
